import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Smile, Paperclip, Users, Video, Phone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import api from '../services/api';
import Modal from './Modal';

interface GroupChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartVideoCall?: (userId: number | null, callType: 'audio' | 'video', callId: number, roomName: string) => void;
}

const GroupChatModal: React.FC<GroupChatModalProps> = ({ isOpen, onClose, onStartVideoCall }) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showReactions, setShowReactions] = useState<number | null>(null);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionUsers, setMentionUsers] = useState<any[]>([]);
  const [mentionQuery, setMentionQuery] = useState('');
  const [highlightMessageId, setHighlightMessageId] = useState<number | null>(null);
  const [editingMessage, setEditingMessage] = useState<{id: number, text: string} | null>(null);
  const [showMessageMenu, setShowMessageMenu] = useState<number | null>(null);
  const [showImageModal, setShowImageModal] = useState<string | null>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  const quickReactions = ['❤️', '😂', '😍', '😢', '😡', '👍', '👎', '😮', '🎉', '🔥'];
  const emojis = [
    '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇',
    '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚',
    '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩',
    '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣',
    '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬',
    '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗',
    '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯',
    '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐',
    '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '😈',
    '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉',
    '👆', '🖕', '👇', '☝️', '👋', '🤚', '🖐️', '✋', '🖖', '👏',
    '🙌', '🤲', '🤝', '🙏', '✍️', '💪', '🦾', '🦿', '🦵', '🦶',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
    '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️'
  ];


  // Online kullanıcıları yükle
  const loadOnlineUsers = async () => {
    try {
      const response = await fetch('/api/group-chat/users/online', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (!response.ok) {
        console.error('Online users loading failed:', response.status);
        return;
      }
      
      const users = await response.json();
      // Sadece online kullanıcıları göster
      const onlineUsers = users.filter((user: any) => user.is_online);
      setMentionUsers(onlineUsers);
    } catch (error) {
      console.error('Error loading online users:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadMessages();
      loadOnlineUsers(); // Sadece online kullanıcıları yükle
      // Biraz bekleyip mesajları yükledikten sonra okundu işaretle
      setTimeout(() => {
        markAsRead();
      }, 500);
    }
  }, [isOpen]);

  useEffect(() => {
    if (messagesEndRef.current && isScrolledToBottom) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isScrolledToBottom]);

  // Scroll pozisyonunu takip et ve görünür mesajları okundu işaretle
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer || !isOpen) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;
      setIsScrolledToBottom(isAtBottom);

      // Eğer en altta isek ve yeni mesajlar varsa okundu işaretle
      if (isAtBottom) {
        setTimeout(() => {
          markAsRead();
        }, 300);
      }
    };

    messagesContainer.addEventListener('scroll', handleScroll);
    // İlk yüklemede de kontrol et
    handleScroll();

    return () => messagesContainer.removeEventListener('scroll', handleScroll);
  }, [isOpen, messages]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadMessages = async () => {
    try {
      const response = await fetch('/api/group-chat', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      const data = await response.json();
      setMessages(data.reverse());

      // Mesajları yükledikten sonra okundu işaretle (eğer modal açıksa)
      if (isOpen) {
        setTimeout(() => {
          markAsRead();
        }, 200);
      }
    } catch (error) {
      console.error('Error loading group messages:', error);
    }
  };

  const markAsRead = async () => {
    try {
      await fetch('/api/group-chat/mark-read', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      // Unread count'u güncelleme eventi gönder
      window.dispatchEvent(new CustomEvent('groupChatRead'));
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };


  const handleSendMessage = async (customMessage?: string) => {
    const messageToSend = customMessage || message;
    if (!messageToSend.trim() || isLoading) return;

    setIsLoading(true);
    try {
      const response = await fetch('/api/group-chat/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ message: messageToSend.trim() })
      });
      
      if (response.ok) {
        const responseData = await response.json();
        setMessage('');
        loadMessages();

        // Dispatch event for new group message (for sound notification)
        window.dispatchEvent(new CustomEvent('newGroupMessage', {
          detail: {
            senderId: user?.id,
            senderName: user?.name,
            message: messageToSend,
            messageData: responseData
          }
        }));

        // Scroll to bottom after sending message
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Start group video call
  const startGroupVideoCall = async () => {
    if (!onStartVideoCall) return;

    try {
      const response = await api.post('/calls/start', {
        callType: 'video',
        isGroupCall: true
      });

      if (response && response.success) {
        // Call the parent callback to open the App-level VideoCallModal
        onStartVideoCall(null, 'video', response.callId, response.roomName);
      }
    } catch (error: any) {
      console.error('Error starting group video call:', error);
      alert(error?.message || 'Grup görüşmesi başlatılamadı');
    }
  };

  // Start group audio call
  const startGroupAudioCall = async () => {
    if (!onStartVideoCall) return;

    try {
      const response = await api.post('/calls/start', {
        callType: 'audio',
        isGroupCall: true
      });

      if (response && response.success) {
        // Call the parent callback to open the App-level VideoCallModal
        onStartVideoCall(null, 'audio', response.callId, response.roomName);
      }
    } catch (error: any) {
      console.error('Error starting group audio call:', error);
      alert(error?.message || 'Grup sesli görüşme başlatılamadı');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (showMentions) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        return;
      }
      if (e.key === 'Escape') {
        setShowMentions(false);
        return;
      }
    }
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMessage(value);
    
    // Mention kontrolü
    const cursorPos = e.target.selectionStart || 0;
    const textBeforeCursor = value.substring(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@([\w\s]*)$/);
    
    if (mentionMatch) {
      const query = mentionMatch[1];
      setMentionQuery(query);
      if (query.length >= 1) {
        searchUsers(query);
        setShowMentions(true);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const searchUsers = async (query: string) => {
    try {
      const response = await fetch(`/api/group-chat/users/search?q=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      const users = await response.json();
      setMentionUsers(users);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const insertMention = (userName: string) => {
    const cursorPos = inputRef.current?.selectionStart || 0;
    const textBeforeCursor = message.substring(0, cursorPos);
    const textAfterCursor = message.substring(cursorPos);
    const mentionMatch = textBeforeCursor.match(/@([\w\s]*)$/);
    
    if (mentionMatch) {
      const beforeMention = textBeforeCursor.substring(0, mentionMatch.index);
      const newMessage = `${beforeMention}@${userName} ${textAfterCursor}`;
      setMessage(newMessage);
      setShowMentions(false);
      
      // Focus geri dön
      setTimeout(() => {
        if (inputRef.current) {
          const newCursorPos = beforeMention.length + userName.length + 2;
          inputRef.current.focus();
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  };

  const renderMessageWithMentions = (text: string) => {
    const mentionRegex = /@([\w\s]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      // Önceki metin
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      // Mention
      parts.push(
        <span key={match.index} className="bg-primary/20 dark:bg-primary/90 text-primary-dark dark:text-accent px-1 rounded font-medium">
          @{match[1]}
        </span>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Kalan metin
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 1 ? parts : text;
  };

  // URL parametresinden mesaj ID'si al ve highlight yap
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const messageId = urlParams.get('messageId');
    if (messageId && isOpen) {
      setHighlightMessageId(parseInt(messageId));
      setTimeout(() => {
        const messageElement = document.getElementById(`message-${messageId}`);
        if (messageElement) {
          messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  }, [isOpen]);

  const addEmoji = (emoji: string) => {
    setMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const addReaction = async (messageId: number, emoji: string) => {
    try {
      const response = await fetch(`/api/group-chat/${messageId}/reaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ emoji })
      });
      
      if (response.ok) {
        loadMessages();
        setShowReactions(null);
      }
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const removeReaction = async (messageId: number) => {
    try {
      const response = await fetch(`/api/group-chat/${messageId}/reaction`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        loadMessages();
      }
    } catch (error) {
      console.error('Error removing reaction:', error);
    }
  };

  // Delete message
  const deleteMessage = async (messageId: number) => {
    if (!confirm('Mesajı silmek istediğinizden emin misiniz?')) return;
    
    try {
      const response = await fetch(`/api/group-chat/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        loadMessages();
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  // Edit message
  const editMessage = async (messageId: number, newText: string) => {
    try {
      const response = await fetch(`/api/group-chat/${messageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ message: newText })
      });
      
      if (response.ok) {
        setEditingMessage(null);
        loadMessages();
      }
    } catch (error) {
      console.error('Error editing message:', error);
    }
  };

  // Dosya yükleme (yapıştırılan ekran görüntüleri dahil; görseller sohbette önizlenir)
  const handleFileUpload = async (file: File) => {
    // Yükleme sürerken tekrar yapıştırma/bırakma aynı dosyayı çoğaltmasın
    if (uploadingRef.current) return;

    // 50MB limit check
    if (file.size > 50 * 1024 * 1024) {
      alert('Dosya boyutu 50MB\'dan büyük olamaz!');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    uploadingRef.current = true;
    try {
      const response = await fetch('/api/group-chat/file', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });
      
      if (response.ok) {
        loadMessages();
      } else {
        alert('Dosya yüklenemedi!');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Dosya yüklenirken hata oluştu!');
    } finally {
      uploadingRef.current = false;
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    
    if (imageItem) {
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (file) {
        handleFileUpload(file);
      }
    }
  };

  // Modal kapanırken okundu işaretle
  const handleClose = () => {
    markAsRead();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} bottomSheet>
      <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg w-full sm:max-w-4xl h-[95dvh] sm:h-[700px] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/20 dark:bg-primary/90 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-primary dark:text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Ekip Sohbeti
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Tüm ekip üyeleri
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={startGroupAudioCall}
              className="p-2 text-gray-400 hover:text-green-500 transition-colors"
              title="Grup Sesli Görüşme"
            >
              <Phone className="w-5 h-5" />
            </button>
            <button
              onClick={startGroupVideoCall}
              className="p-2 text-gray-400 hover:text-primary transition-colors"
              title="Grup Görüntülü Görüşme"
            >
              <Video className="w-5 h-5" />
            </button>
            <button
              onClick={handleClose}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>


        {/* Messages */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4" onPaste={handlePaste}>
          {messages.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Henüz mesaj yok. İlk mesajı gönderin!
              </p>
            </div>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.sender_id === user?.id;
              return (
                <div
                  key={msg.id}
                  id={`message-${msg.id}`}
                  className={`flex items-start space-x-2 ${isOwn ? 'flex-row-reverse space-x-reverse' : ''} ${
                    highlightMessageId === msg.id ? 'bg-yellow-100 dark:bg-yellow-900/20 rounded-lg p-2 -m-2' : ''
                  }`}
                >
                  <img
                    className="w-8 h-8 rounded-full flex-shrink-0"
                    src={msg.sender_avatar || 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'}
                    alt={msg.sender_name}
                  />
                  
                  <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} max-w-md`}>
                    <div className={`text-xs text-gray-500 dark:text-gray-400 mb-1 px-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                      {isOwn ? 'Sen' : msg.sender_name}
                    </div>
                    
                    <div className="relative group">
                      <div
                        className={`px-4 py-2 rounded-2xl max-w-full break-words ${
                          isOwn
                            ? 'bg-primary text-white rounded-br-md'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-md'
                        }`}
                      >
                        {msg.message.startsWith('[Görsel:') ? (
                          <div>
                            <p className="text-xs text-gray-400 mb-2">
                              {msg.message.split('] ')[0] + ']'}
                            </p>
                            <img 
                              src={msg.message.split('] ')[1]} 
                              alt="Paylaşılan görsel"
                              className="max-w-xs max-h-64 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => setShowImageModal(msg.message.split('] ')[1])}
                              style={{ filter: 'none', opacity: 1 }}
                            />
                          </div>
                        ) : msg.message.startsWith('[Dosya:') ? (
                          <div>
                            <p className="text-xs text-gray-400 mb-2">
                              {msg.message.split('] ')[0] + ']'}
                            </p>
                            <a 
                              href={`${msg.message.split('] ')[1]}`}
                              download
                              className="inline-flex items-center px-3 py-2 bg-primary/20 dark:bg-primary/90 text-primary-dark dark:text-accent rounded-lg hover:bg-primary/30 dark:hover:bg-primary-dark transition-colors"
                            >
                              📄 {msg.message.split('] ')[0].replace('[Dosya: ', '').replace(']', '')}
                            </a>
                          </div>
                        ) : editingMessage?.id === msg.id ? (
                          <div className="w-full">
                            <input
                              type="text"
                              value={editingMessage.text}
                              onChange={(e) => setEditingMessage({...editingMessage, text: e.target.value})}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  editMessage(msg.id, editingMessage.text);
                                } else if (e.key === 'Escape') {
                                  setEditingMessage(null);
                                }
                              }}
                              className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                              autoFocus
                            />
                            <div className="flex space-x-2 mt-1">
                              <button
                                onClick={() => editMessage(msg.id, editingMessage.text)}
                                className="text-xs text-primary hover:text-primary-dark"
                              >
                                Kaydet
                              </button>
                              <button
                                onClick={() => setEditingMessage(null)}
                                className="text-xs text-gray-600 hover:text-gray-800"
                              >
                                İptal
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm leading-relaxed">
                              {renderMessageWithMentions(msg.message)}
                            </p>
                            {msg.edited_at && (
                              <p className="text-xs text-gray-400 mt-1 italic">düzenlendi</p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Reactions */}
                      {msg.reactions && msg.reactions.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Object.entries(
                            msg.reactions.reduce((acc: any, r: any) => {
                              if (!acc[r.emoji]) acc[r.emoji] = [];
                              acc[r.emoji].push(r);
                              return acc;
                            }, {})
                          ).map(([emoji, reactions]: [string, any]) => {
                            const hasUserReaction = reactions.some((r: any) => r.user_id === user?.id);
                            return (
                              <button
                                key={emoji}
                                onClick={() => hasUserReaction ? removeReaction(msg.id) : addReaction(msg.id, emoji)}
                                className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                  hasUserReaction 
                                    ? 'bg-primary/20 dark:bg-primary/90 border-primary/50 dark:border-primary-dark' 
                                    : 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                                title={reactions.map((r: any) => r.user_name).join(', ')}
                              >
                                {emoji} {reactions.length}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Quick reaction button and message menu */}
                      <div className={`absolute top-0 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1 ${
                        isOwn ? '-left-16' : '-right-16'
                      }`}>
                        <button
                          onClick={() => setShowReactions(showReactions === msg.id ? null : msg.id)}
                          className="p-1 text-gray-400 hover:text-yellow-500 transition-colors"
                          title="Tepki ver"
                        >
                          😊
                        </button>
                        
                        {isOwn && (
                          <button
                            onClick={() => setShowMessageMenu(showMessageMenu === msg.id ? null : msg.id)}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Menü"
                          >
                            ⋮
                          </button>
                        )}
                        
                        {showReactions === msg.id && (
                          <div className="absolute top-8 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 z-20 grid grid-cols-5 gap-1 w-48">
                            {quickReactions.map(emoji => (
                              <button
                                key={emoji}
                                onClick={() => addReaction(msg.id, emoji)}
                                className="text-lg hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-1 transition-colors flex items-center justify-center h-8 w-8"
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {showMessageMenu === msg.id && isOwn && (
                          <div className={`absolute top-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-20 ${
                            isOwn ? 'left-0' : 'right-0'
                          }`}>
                            <button
                              onClick={() => {
                                setEditingMessage({id: msg.id, text: msg.message});
                                setShowMessageMenu(null);
                              }}
                              className="flex items-center px-3 py-2 text-sm text-primary hover:bg-primary/10 dark:hover:bg-primary/20 w-full text-left"
                            >
                              ✏️ Düzenle
                            </button>
                            <button
                              onClick={() => {
                                deleteMessage(msg.id);
                                setShowMessageMenu(null);
                              }}
                              className="flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left"
                            >
                              🗑️ Sil
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className={`flex items-center mt-1 px-1 ${isOwn ? 'flex-row-reverse' : ''}`}>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {new Date(msg.created_at).toLocaleTimeString('tr-TR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="relative">
            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div 
                ref={emojiPickerRef}
                className="absolute bottom-16 left-0 right-0 sm:right-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 sm:p-4 sm:w-80 max-h-52 overflow-y-auto z-10"
              >
                <div className="grid grid-cols-10 gap-2">
                  {emojis.map((emoji, index) => (
                    <button
                      key={index}
                      onClick={() => addEmoji(emoji)}
                      className="text-xl hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-1 transition-colors"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Mention Dropdown */}
            {showMentions && mentionUsers.length > 0 && (
              <div className="absolute bottom-16 left-16 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-40 overflow-y-auto z-30 w-64">
                {mentionUsers.map((mentionUser) => (
                  <button
                    key={mentionUser.id}
                    onClick={() => insertMention(mentionUser.name)}
                    className="w-full flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                  >
                    <img
                      className="w-6 h-6 rounded-full"
                      src={mentionUser.avatar || 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'}
                      alt={mentionUser.name}
                    />
                    <span className="text-sm text-gray-900 dark:text-gray-100">{mentionUser.name}</span>
                  </button>
                ))}
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="w-12 h-12 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center justify-center"
              >
                <Smile className="w-5 h-5" />
              </button>
              
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-12 h-12 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center justify-center"
                title="Dosya Ekle"
              >
                <Paperclip className="w-5 h-5" />
              </button>
              
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(file);
                }}
              />
              
              <input
                type="text"
                value={message}
                ref={inputRef}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Ekip sohbetine mesaj yazın..."
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={!message.trim() || isLoading}
                className="w-12 h-12 bg-primary text-white rounded-full hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[70] p-4">
          <div className="relative max-w-full max-h-full">
            <button
              onClick={() => setShowImageModal(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 rounded-full p-2"
            >
              <X className="w-6 h-6" />
            </button>
            <button
              onClick={() => {
                const link = document.createElement('a');
                link.href = showImageModal;
                link.download = 'screenshot.png';
                link.click();
              }}
              className="absolute top-4 left-4 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 rounded-full p-2"
              title="İndir"
            >
              💾
            </button>
            <img
              src={showImageModal}
              alt="Büyütülmüş görsel"
              className="max-w-full max-h-full object-contain rounded-lg"
              style={{ filter: 'none', opacity: 1 }}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default GroupChatModal;